import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import classNames from "classnames";
import * as React from "react";

import styles from "./Dialog.module.css";

type BaseUIClassName<State> =
  | string
  | ((state: State) => string | undefined)
  | undefined;

/**
 * Merges a house class with a consumer's. Base UI's `className` is either a
 * string or a function of the part's state, so both forms have to survive.
 */
function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
): BaseUIClassName<State> {
  return typeof className === "function"
    ? (state: State) => classNames(houseClass, className(state))
    : classNames(houseClass, className);
}

/** A plain dialog: Escape and a click on the backdrop dismiss it. */
export interface DialogRoleDialogProps extends BaseDialog.Root.Props {
  /** @default "dialog" */
  role?: "dialog";
}

/**
 * An alert dialog, for a question the page cannot go on without. Always
 * modal, so Base UI drops `modal` and `disablePointerDismissal`: a click on
 * the backdrop never dismisses it, Escape and a `Close` still do.
 */
export interface DialogRoleAlertDialogProps extends BaseAlertDialog.Root.Props {
  role: "alertdialog";
}

export type DialogRootProps =
  | DialogRoleDialogProps
  | DialogRoleAlertDialogProps;

/**
 * Only the Base UI root differs between the two roles: every other part of
 * Base UI's alert dialog is the dialog's own part, re-exported.
 */
function DialogRoot(props: DialogRootProps) {
  if (props.role === "alertdialog") {
    const { role: _alertdialog, ...rootProps } = props;
    return <BaseAlertDialog.Root {...rootProps} />;
  }
  const { role: _dialog, ...rootProps } = props;
  return <BaseDialog.Root {...rootProps} />;
}

export interface DialogTriggerProps extends BaseDialog.Trigger.Props {}

/**
 * The control that opens the dialog. Omit it and drive `open`/`onOpenChange`
 * on the root instead, for a dialog raised from something that is not a
 * button of its own.
 */
const Trigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  function DialogTriggerPart(props, ref) {
    return <BaseDialog.Trigger {...props} ref={ref} />;
  },
);

/**
 * Base UI `Popup` props, plus the two custom properties the popup reads off
 * `className`: `--dialog-max-width` (default `32rem`) and
 * `--dialog-max-height` (default `100%`).
 */
export interface DialogContentProps extends BaseDialog.Popup.Props {}

/**
 * The modal surface, fusing Base UI's Portal, Backdrop, Viewport, and Popup.
 *
 * The curated part, as `Popover.Content` is: `Popup` props pass through
 * (`initialFocus`, `finalFocus`, `render`, `aria-label`, …), while the
 * backdrop, the centering, and the z-index are system decisions and are not
 * exposed. There is no `side` or `align` — a dialog is centred, not anchored,
 * which is the one place this parts with `Popover`.
 *
 * Size it via `className`: `--dialog-max-width` for width, `--dialog-max-height`
 * for a height cap. It never exceeds the viewport regardless, and the popup is
 * a column flexbox, so a body given `overflow: auto` scrolls under a header
 * that stays put.
 *
 * The popup clips its content, which is what keeps a long body inside the
 * height cap and the rounded corners.
 */
const Content = React.forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContentPart({ className, children, ...popupProps }, ref) {
    return (
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={styles.backdrop} />
        <BaseDialog.Viewport className={styles.viewport}>
          <BaseDialog.Popup
            {...popupProps}
            ref={ref}
            className={withHouseClass(styles.popup, className)}
          >
            {children}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    );
  },
);

export interface DialogTitleProps extends BaseDialog.Title.Props {}

/** A visible heading that names the dialog, in place of an `aria-label`. */
const Title = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
  function DialogTitlePart({ className, ...props }, ref) {
    return (
      <BaseDialog.Title
        {...props}
        ref={ref}
        className={withHouseClass(styles.title, className)}
      />
    );
  },
);

export interface DialogDescriptionProps extends BaseDialog.Description.Props {}

/** Secondary body text under the `Title`, describing the dialog. */
const Description = React.forwardRef<
  HTMLParagraphElement,
  DialogDescriptionProps
>(function DialogDescriptionPart({ className, ...props }, ref) {
  return (
    <BaseDialog.Description
      {...props}
      ref={ref}
      className={withHouseClass(styles.description, className)}
    />
  );
});

export interface DialogCloseProps extends BaseDialog.Close.Props {}

/** A button inside the dialog that dismisses it. */
const Close = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  function DialogClosePart(props, ref) {
    return <BaseDialog.Close {...props} ref={ref} />;
  },
);

/**
 * A modal panel centred over the page, dimming what is behind it.
 *
 * The same shape as `Popover` minus the anchoring: a root, a `Trigger`, one
 * fused `Content`, and `Title` / `Description` / `Close` parts. Reach for it
 * when a surface needs the page's full attention and room to lay content out;
 * reach for `Popover` when the surface belongs to the control that opened it,
 * and for `ConfirmDialog` when it is one question with two answers.
 *
 * Focus is trapped in the popup while open and returns to the trigger on
 * close. Escape and a click on the backdrop dismiss.
 *
 * `role="alertdialog"` (Kumo's prop, same two values) turns it into an alert
 * dialog: the popup takes `role="alertdialog"`, it is always modal, and the
 * backdrop stops dismissing it, so only Escape or a `Close` inside can. Reach
 * for it when the dialog asks a question that needs an answer, such as a
 * destructive confirmation that carries more than `ConfirmDialog` holds.
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <Dialog.Trigger render={<RobotoButton variant="ghost">View sample</RobotoButton>} />
 *   <Dialog.Content>
 *     <Dialog.Title>Sample payload</Dialog.Title>
 *     <Dialog.Description>What this trigger will see.</Dialog.Description>
 *     <SampleBody />
 *   </Dialog.Content>
 * </Dialog>
 * ```
 */
export const Dialog = Object.assign(DialogRoot, {
  Trigger,
  Content,
  Title,
  Description,
  Close,
});
